import { CommonModule } from '@angular/common';
import type { SimpleChanges } from '@angular/core';
import { ChangeDetectorRef, Component, ElementRef, Input, Renderer2, output } from '@angular/core';

import moment from 'moment-timezone';
import type { Observable, Subject, Subscription } from 'rxjs';
import { timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
import type { Requisition } from '@core/requisitions-types';
import { RequisitionStatus } from '@core/requisitions-types';
import {
  ButtonComponent,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  IconButtonComponent,
  IconComponent,
  OverlayOriginDirective,
  RtTooltip,
  TimeAgoAsyncPipe,
} from '@core/user-interface-legacy';
import { LifeCycle } from '@core/utils-legacy';

import type { RequestApprovalDisabled } from '../../../facades/single-requisition/requisitions-general.facade';

@Component({
  selector: 'rt-requisitions-toolbar',
  templateUrl: './requisitions-toolbar.component.html',
  styleUrls: ['./requisitions-toolbar.component.less'],
  imports: [
    ButtonComponent,
    CommonModule,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    RtTooltip,
    TimeAgoAsyncPipe,
    TranslatePipe,
  ],
})
export class RequisitionsToolbarComponent {
  @Input() public requisition: Requisition;
  @Input() public isFormPristine: boolean = true;
  @Input() public hideManageActionsUnderMore: boolean = true;
  @Input() public hasManageActions: boolean;
  @Input() public hasManageActionsDisabled: boolean;
  @Input() public hasApproverActions: boolean;
  @Input() public canCreateJob: boolean;
  @Input() public hasSave: boolean = true;
  @Input() public requestApprovalDisabled: Observable<RequestApprovalDisabled>;

  public save = output<void>();
  public requestApproval = output<void>();
  public cancelRequest = output<void>();
  public createJob = output<void>();
  public duplicate = output<void>();
  public archive = output<void>();
  public retrieve = output<void>();
  public delete = output<void>();
  public approveRequest = output<void>();
  public rejectRequest = output<void>();

  public requestApprovalButtonDisabled: boolean;
  public requestApprovalButtonDisabledMessage: string;

  public RequisitionStatus: typeof RequisitionStatus = RequisitionStatus;
  public DropdownWidth = DropdownWidth;

  private requestApprovalDisabledSubscription: Subscription | null = null;
  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private cdRef: ChangeDetectorRef,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.requestApprovalDisabled?.currentValue) {
      this.subscribeToRequestApprovalDisabledChanges();
    }
  }

  public ngAfterViewInit(): void {
    this.setTopOffsetForStickyPosition();
  }

  public isOlderThanHour(): boolean {
    return moment(this.requisition.updatedAt).isBefore(moment().subtract(1, 'h'));
  }

  private setTopOffsetForStickyPosition(): void {
    timer(100).subscribe(() => {
      const topOffset = this.elementRef.nativeElement.offsetParent.firstChild.offsetHeight;
      this.renderer.setStyle(this.elementRef.nativeElement, 'top', `${topOffset - 1}px`);
    });
  }

  private subscribeToRequestApprovalDisabledChanges() {
    if (this.requestApprovalDisabledSubscription) {
      this.requestApprovalDisabledSubscription.unsubscribe();
      this.requestApprovalDisabledSubscription = null;
    }

    this.requestApprovalDisabledSubscription = this.requestApprovalDisabled
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(requestApprovalDisabled => {
        this.requestApprovalButtonDisabled = !!requestApprovalDisabled;
        this.requestApprovalButtonDisabledMessage = requestApprovalDisabled
          ? requestApprovalDisabled.message
          : '';
        this.cdRef.detectChanges();
      });
  }
}
